// Purpose: Let an invited candidate review and confirm the entry already registered by an event manager.
// Dependencies: Token-bound callable projections, event/auth contexts, and normal shared account signup.
// Behavior: Review works independently of online submission settings; it never creates a second entry or changes authority.
import React, { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, functions } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import { useAuth } from "../../auth_context";
import { getEntryCandidateInvite } from "../../utils/entry_candidate_invite";
import { completeEmailRegistration } from "../../services/email_registration";
import EntryAccountSignup from "../../components/entry_account_signup";
import FullPageSpinner from "../../components/FullPageSpinner";
import SeoHead from "../../components/seo_head";
import { showStatus } from "../../utils/status_helpers";

export default function EntryCandidateReview({ entryId }) {
  const { eventId, eventslug, eventLoading, ready } = useEventContext({ redirectIfNotFound: true });
  const { user } = useAuth();
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const registrationUserRef = useRef(null);
  const registrationCompleteRef = useRef(false);
  const token = getEntryCandidateInvite(eventslug, entryId);

  useEffect(() => {
    if (!ready || eventLoading || !eventId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    setReview(null);
    if (!token) { setLoadError(true); setLoading(false); return; }
    httpsCallable(functions, "getEntryCandidateReview")({ eventId, entryId, token })
      .then(({ data }) => {
        if (!cancelled) { setReview(data); setConfirmed(data.confirmed === true); }
      })
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, eventslug, entryId, ready, eventLoading, token, reloadKey]);

  const confirmDetails = async () => {
    if (busy || !review) return;
    const registering = createAccount && (!user || registrationUserRef.current);
    if (registering && !registrationUserRef.current && (password.trim().length < 6 || !agreedToTerms)) {
      showStatus(setStatus, { type: "warning", message: "Use a password with at least 6 characters and accept the Terms & Conditions." });
      return;
    }
    setBusy(true);
    try {
      if (registering) {
        if (!registrationUserRef.current) {
          registrationUserRef.current = (await createUserWithEmailAndPassword(auth, review.email, password)).user;
          setPassword("");
        }
        if (!registrationCompleteRef.current) {
          await completeEmailRegistration(registrationUserRef.current, {
            firstName: review.firstName, lastName: review.lastName, agreedToTerms,
          });
          registrationCompleteRef.current = true;
        }
      }
      await httpsCallable(functions, "confirmEntryCandidateReview")({ eventId, entryId, token, reviewRevision: review.reviewRevision });
      setConfirmed(true);
      showStatus(setStatus, { type: "success", message: "Your entry details are confirmed. Thank you!" });
    } catch (error) {
      const existingAccount = error.code === "auth/email-already-in-use";
      const signupMessage = {
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/password-does-not-meet-requirements": "Choose a password that meets the account security requirements.",
        "auth/too-many-requests": "Too many attempts. Please try again later.",
      }[error.code];
      showStatus(setStatus, { type: existingAccount ? "warning" : "error", message: existingAccount
        ? "This email is already registered. Please log in with your existing account."
        : signupMessage ? signupMessage
        : error.code === "functions/failed-precondition" ? "Entry details changed. Reload Entry Details, review again, then confirm."
        : "Unable to finish confirmation. Please retry, or reopen your invitation email if the link has expired." });
    } finally { setBusy(false); }
  };

  if (!ready || eventLoading || loading) return <FullPageSpinner message="Loading your entry..." />;
  return <>
    <SeoHead title="Review Your Entry | Let's Go Vote" path={`/${eventslug}/public_entry`} noindex={true} />
    <section id="top-content" className="container py-4" style={{ maxWidth: "800px" }}>
      <h1>Review Your Entry</h1>
      {token && <button type="button" className="btn btn-outline-secondary mb-3" disabled={busy}
        onClick={() => setReloadKey((value) => value + 1)}>Reload Entry Details</button>}
      {loadError || !review ? <div className="alert alert-danger" role="alert">
        This review link is unavailable or expired. Reopen the original invitation email. If it still fails, ask the event manager for a new invitation.
      </div> : <>
        {status?.message && <div role="alert" className={`alert ${status.type === "success" ? "alert-success" : status.type === "warning" ? "alert-warning" : "alert-danger"}`}>{status.message}</div>}
        <p>The event owner has registered your entry for <strong>{review.eventName}</strong>. Check the information below.</p>
        <div className="card shadow-sm"><div className="card-body">
          <dl className="row mb-0">
            {Object.entries({ "Registration Number": review.regcnt, "Entry Title": review.entryTitle,
              "First Name": review.firstName, "Last Name": review.lastName, Email: review.email,
              Category: review.categoryName || "No category (default)", Description: review.description || "Not provided",
              Facebook: review.facebook || "Not provided", Instagram: review.instagram || "Not provided" }).map(([label, value]) => (
              <React.Fragment key={label}><dt className="col-sm-4">{label}</dt><dd className="col-sm-8 text-break">{String(value || "")}</dd></React.Fragment>
            ))}
          </dl>
          {review.imgUrl && <img src={review.imgUrl} alt={review.entryTitle || "Your entry"} className="img-fluid rounded my-3" style={{ maxHeight: "300px" }} />}
          <p className="form-text">Need a correction? Contact the event manager before confirming.</p>
          {review.contactEmail && <a href={`mailto:${encodeURIComponent(review.contactEmail)}`}>Contact the event manager</a>}
          <EntryAccountSignup user={user} accountCreated={Boolean(registrationUserRef.current)}
            createAccount={createAccount} onCreateAccountChange={(checked) => { setCreateAccount(checked); setPassword(""); }}
            password={password} onPasswordChange={setPassword} agreedToTerms={agreedToTerms} onTermsChange={setAgreedToTerms} disabled={busy} />
          {registrationCompleteRef.current && <div className="alert alert-info mt-3">Your account was created. Check your email to verify before voting. <a href="/dashboard?verifyRequired=1">Continue to your account</a></div>}
          {confirmed && <div className="alert alert-success mt-3">Entry details confirmed. Your existing registration is saved.</div>}
          <button type="button" className="btn btn-primary mt-3" disabled={busy || (confirmed && (!createAccount || registrationCompleteRef.current))}
            onClick={confirmDetails}>{busy ? "Saving..." : createAccount && (!user || registrationUserRef.current) && !registrationCompleteRef.current ? "Create Account and Confirm Details" : "Confirm Entry Details"}</button>
        </div></div>
      </>}
    </section>
  </>;
}
